import { Fragment } from "react";
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import { RevButton, RevLink, RevTooltip } from "@revmatics/catalyst";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { useFlags } from "launchdarkly-react-client-sdk";

type Props = {
  icon: string;
  title: string;
  children: React.ReactNode;
  canEdit?: boolean;
  editPathname?: string;
  editSearch?: string;
  isEditMode?: boolean;
  isEditDisabled?: boolean;
  isEditLoading?: boolean;
  onToggleEdit?: () => void;
  onCancel?: () => void;
};

export const DetailSectionCard = ({
  icon,
  title,
  children,
  canEdit = false,
  editPathname = "",
  editSearch = "",
  isEditMode,
  isEditDisabled = false,
  isEditLoading = false,
  onToggleEdit,
  onCancel,
}: Props) => {
  const { personaDetails: ffPersonaDetails } = useFlags();
  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-4 items-start flex-col sm:flex-row">
        <div className="flex-shrink-0 w-20 h-20 bg-primary-50 rounded-lg flex items-center justify-center">
          <img src={icon} alt={title} className="w-14 h-14" />
        </div>
        <div className="flex-grow">
          <div className="flex justify-between items-center w-full mb-4">
            <h5>{title}</h5>
            {editPathname && (
              <div className="flex gap-2 items-center justify-center">
                {!(!ffPersonaDetails && editPathname.includes(`personas`)) ? (
                  <RevLink
                    text="Edit"
                    pathname={editPathname}
                    search={editSearch}
                  />
                ) : (
                  <span className="typography-body-sm">Edit</span>
                )}

                <div className="flex justify-center text-monochrome-600">
                  <RevTooltip content={`Go to ${title}`} position="top">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-xs hover:text-primary-500"
                    />
                  </RevTooltip>
                </div>
              </div>
            )}
            {canEdit && !isEditMode && (
              <Menu>
                <MenuButton
                  disabled={isEditDisabled}
                  className={`flex items-center justify-center rounded  w-6 h-6 text-md
                    ${isEditDisabled ? `text-monochrome-300` : `text-monochrome-700 hover:bg-monochrome-100 focus:bg-monochrome-200 cursor-pointer`}
                    `}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </MenuButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems
                    transition
                    anchor="bottom end"
                    className="absolute md:right-0 w-[191px] origin-top-right rounded-lg bg-white py-1 text-sm shadow-elevation-2 focus:outline-none z-10"
                  >
                    <MenuItem>
                      <button
                        className="group flex w-full items-center 
                          text-monochrome-900 px-3 py-1 hover:bg-monochrome-50"
                        onClick={onToggleEdit}
                      >
                        Direct Manual Edit
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Transition>
              </Menu>
            )}
          </div>
          {children}
        </div>
      </div>
      {isEditMode && (
        <div className="flex justify-end gap-3">
          <RevButton text="Cancel" variant="secondary" handleClick={onCancel} />
          <RevButton text="Save" type="submit" isLoading={isEditLoading} />
        </div>
      )}
    </div>
  );
};
